/*
 * Shared app nav — the sticky navy mot-r bar shown across every route.
 *
 * Layout:
 *   left   — mot-r SVG logo (links to /) + optional progress meter
 *   center — first-class tool menu (Plan Builder, Workbench, Stay or Go,
 *            Analytics for analyst+admin, Admin links for admin only)
 *   right  — auth affordance (Sign in OR user email + Sign out)
 *
 * Internal SPA navigation uses react-router <Link> when a Router is
 * available (SWA build). The Squarespace single-file bundles have no
 * Router and no SWA auth, so we fall back to plain <a> elements and
 * hide the auth section entirely there.
 *
 * The optional progress bar is driven by useAppNavProgress() — Plan
 * Builder calls setAppNavProgress() from a useEffect as it navigates
 * through diagnostic sections. All other routes leave it null (hidden).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useInRouterContext,
  useLocation,
} from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  getCurrentPrincipal,
  hasRole,
  loginUrl,
  logoutUrl,
  type SwaClientPrincipal,
} from '@shared/auth/swaPrincipal';
import { useAppNavProgress } from './appNavProgress';
import './AppShell.css';

function NavLink({
  to,
  className,
  children,
  ariaLabel,
  ariaCurrent,
}: {
  to: string;
  className?: string;
  ariaLabel?: string;
  ariaCurrent?: 'page';
  children: ReactNode;
}) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return (
      <Link to={to} className={className} aria-label={ariaLabel} aria-current={ariaCurrent}>
        {children}
      </Link>
    );
  }
  return (
    <a href={to} className={className} aria-label={ariaLabel} aria-current={ariaCurrent}>
      {children}
    </a>
  );
}

interface ToolNavItem {
  label: string;
  to: string;
  match: (path: string) => boolean;
}

const PUBLIC_TOOLS: ReadonlyArray<ToolNavItem> = [
  {
    label: 'Plan Builder',
    to: '/plan-builder',
    match: (p) => p === '/plan-builder',
  },
  {
    label: 'Workbench',
    to: '/ddw',
    match: (p) => p === '/ddw',
  },
  {
    label: 'Stay or Go?',
    to: '/stay-or-go',
    match: (p) => p === '/stay-or-go',
  },
];

const ADMIN_TOOLS: ReadonlyArray<ToolNavItem> = [
  {
    label: 'DDW Admin',
    to: '/admin/ddw',
    match: (p) => p === '/admin/ddw',
  },
  {
    label: 'Stay or Go Admin',
    to: '/admin/stay-or-go',
    match: (p) => p === '/admin/stay-or-go',
  },
];

const ANALYTICS_ITEM: ToolNavItem = {
  label: 'Analytics',
  to: '/analytics',
  match: (p) => p.startsWith('/analytics'),
};

// In the SWA build a Router wraps everything, so useLocation drives
// active-link state. In the Squarespace single-file build there is no
// Router; we read window.location.pathname once at mount (the page
// reloads on every cross-tool navigation, so this stays accurate).
function RoutedPath({ render }: { render(path: string): ReactNode }) {
  const path = useLocation().pathname;
  return <>{render(path)}</>;
}

function StaticPath({ render }: { render(path: string): ReactNode }) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return <>{render(path)}</>;
}

export function AppNav() {
  const inRouter = useInRouterContext();
  const PathProvider = inRouter ? RoutedPath : StaticPath;
  return <PathProvider render={(path) => <AppNavInner path={path} inRouter={inRouter} />} />;
}

function AppNavInner({ path, inRouter }: { path: string; inRouter: boolean }) {
  const progress = useAppNavProgress();

  // Auth principal — only meaningful in the SWA build (Router present).
  // Squarespace bundles skip the fetch; their auth UI is hidden anyway.
  const [principal, setPrincipal] = useState<SwaClientPrincipal | null>(null);
  useEffect(() => {
    if (!inRouter) return;
    void getCurrentPrincipal().then(setPrincipal);
  }, [inRouter]);

  const showAnalytics = hasRole(principal, 'admin', 'analyst');
  const showAdmin = hasRole(principal, 'admin');

  const items = useMemo<ToolNavItem[]>(() => {
    const list: ToolNavItem[] = [...PUBLIC_TOOLS];
    if (showAnalytics) list.push(ANALYTICS_ITEM);
    if (showAdmin) list.push(...ADMIN_TOOLS);
    return list;
  }, [showAnalytics, showAdmin]);

  return (
    <nav className="app-nav" aria-label="Main">
      <div className="app-nav-left">
        <NavLink to="/" className="app-nav-logo" ariaLabel="mot-r tools home">
          <svg className="app-nav-logo-svg" viewBox="50 10 515 112" xmlns="http://www.w3.org/2000/svg">
            <path fill="#ed1c24" d="M134.35,95.76c0,10.91-8.84,19.74-19.75,19.74h-40.66c-10.9,0-19.75-8.84-19.75-19.74v-40.67c0-10.91,8.85-19.74,19.75-19.74h40.66c10.91,0,19.75,8.84,19.75,19.74v40.67Z"/>
            <path fill="#fff" d="M69.47,61.96v26.93h8.71l-2.13-11.55h0c-.61-3.34,1.6-6.55,4.94-7.17,3.34-.62,6.55,1.6,7.17,4.94h0l2.54,13.79h7.99l-2.13-11.55h0c-.62-3.34,1.6-6.55,4.94-7.17,3.34-.62,6.55,1.6,7.17,4.94h0l2.54,13.79h7.84v-26.93h-49.59v-.02Z"/>
            <path fill="#fff" d="M225.54,115.25h-22.23v-47.6c0-5.88-.98-10.29-2.95-13.23s-5.07-4.41-9.29-4.41c-5.69,0-9.82,2.09-12.39,6.27-2.58,4.18-3.86,11.05-3.86,20.63v38.34h-22.23V33.76h16.98l2.99,10.42h1.24c2.19-3.74,5.35-6.67,9.48-8.78,4.13-2.11,8.87-3.17,14.21-3.17,12.2,0,20.46,3.98,24.78,11.95h1.97c2.19-3.79,5.41-6.73,9.66-8.82s9.05-3.13,14.4-3.13c9.23,0,16.22,2.37,20.96,7.11s7.11,12.33,7.11,22.78v53.13h-22.3v-47.6c0-5.88-.98-10.29-2.95-13.23s-5.07-4.41-9.29-4.41c-5.44,0-9.51,1.94-12.21,5.83s-4.05,10.06-4.05,18.51v40.89h-.03Z"/>
            <path fill="#fff" d="M373.21,74.36c0,13.27-3.5,23.64-10.5,31.12s-16.74,11.22-29.23,11.22c-7.82,0-14.72-1.71-20.7-5.14s-10.57-8.35-13.78-14.76-4.81-13.9-4.81-22.45c0-13.31,3.47-23.66,10.42-31.05,6.95-7.39,16.72-11.08,29.3-11.08,7.82,0,14.72,1.7,20.7,5.1s10.57,8.28,13.78,14.65,4.81,13.82,4.81,22.38h.01ZM316.87,74.36c0,8.07,1.32,14.16,3.97,18.29s6.96,6.2,12.94,6.2,10.19-2.05,12.79-6.16,3.9-10.22,3.9-18.33-1.31-14.12-3.94-18.15c-2.62-4.03-6.92-6.05-12.9-6.05s-10.2,2-12.83,6.01-3.94,10.07-3.94,18.19h0Z"/>
            <path fill="#fff" d="M425.91,98.99c3.89,0,8.55-.85,13.99-2.55v16.55c-5.54,2.48-12.34,3.72-20.41,3.72-8.89,0-15.37-2.25-19.42-6.74-4.06-4.49-6.09-11.24-6.09-20.23v-39.29h-10.64v-9.4l12.25-7.43,6.41-17.2h14.21v17.35h22.81v16.69h-22.81v39.29c0,3.16.89,5.49,2.66,7s4.12,2.26,7.03,2.26v-.02Z"/>
            <path fill="#fff" d="M449.16,84.34v-18.22h39.14v18.22h-39.14,0Z"/>
            <path fill="#fff" d="M549.89,32.23c3.01,0,5.52.22,7.51.66l-1.68,20.85c-1.8-.49-3.98-.73-6.56-.73-7.09,0-12.62,1.82-16.58,5.47s-5.94,8.75-5.94,15.31v41.47h-22.23V33.77h16.84l3.28,13.7h1.09c2.53-4.57,5.94-8.25,10.24-11.04s8.98-4.19,14.03-4.19h0Z"/>
          </svg>
        </NavLink>
        <div
          className={`app-nav-progress${progress ? ' visible' : ''}`}
          role={progress ? 'progressbar' : undefined}
          aria-valuemin={progress ? 0 : undefined}
          aria-valuemax={progress ? 100 : undefined}
          aria-valuenow={progress ? progress.value : undefined}
          aria-label={progress?.label}
        >
          {progress && (
            <>
              <span className="app-nav-progress-label">{progress.label}</span>
              <div className="app-nav-progress-track">
                <div className="app-nav-progress-fill" style={{ width: `${progress.value}%` }} />
              </div>
            </>
          )}
        </div>
      </div>

      <ul className="app-nav-menu" aria-label="Tools">
        {items.map((item) => {
          const active = item.match(path);
          return (
            <li key={item.to}>
              <NavLink to={item.to} ariaCurrent={active ? 'page' : undefined}>
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {inRouter && (
        <div className="app-nav-auth">
          {principal ? (
            <>
              <span className="app-nav-auth-user" title={principal.userDetails}>
                {principal.userDetails}
              </span>
              <a className="app-nav-auth-link" href={logoutUrl()}>
                Sign out
              </a>
            </>
          ) : (
            <a className="app-nav-auth-link" href={loginUrl()}>
              Sign in
            </a>
          )}
        </div>
      )}
    </nav>
  );
}
